import React, { useEffect, useMemo, useState } from "react";
import { useSpecialistProfile } from "../state/SpecialistProfileContext";

type ConditionRow = {
  ConditionID: number;
  Name: string | null;
};

type InvestigationRow = {
  InvestigationID: number;
  Name: string | null;
};

const csvToArray = (val: any): string[] => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(String).filter(Boolean);

  if (typeof val === "string") {
    return val
      .split(",")
      .map((x) => x.trim())
      .filter((x) => x.length > 0);
  }

  return [];
};

const arrayToCsv = (arr: string[]) =>
  arr
    .map((x) => String(x).trim())
    .filter((x) => x.length > 0)
    .join(",");

const TabConditionsInvestigations: React.FC = () => {
  const { formState, updateConditionsField, updateReferralField } =
    useSpecialistProfile();

  const { primaryConditionIds, excludedConditionIds } = formState.conditions;

  // investigations are stored as CSV string in referral.requiredInvestigationIds (existing behavior)
  const selectedInvestigationIds: string[] = useMemo(() => {
    return csvToArray(formState?.referral?.requiredInvestigationIds);
  }, [formState?.referral?.requiredInvestigationIds]);

  // CONDITIONS
  const [conditions, setConditions] = useState<ConditionRow[]>([]);
  const [conditionsLoading, setConditionsLoading] = useState(false);
  const [conditionsError, setConditionsError] = useState<string>("");

  const [primarySearch, setPrimarySearch] = useState("");
  const [excludedSearch, setExcludedSearch] = useState("");

  // INVESTIGATIONS
  const [investigations, setInvestigations] = useState<InvestigationRow[]>([]);
  const [investigationsLoading, setInvestigationsLoading] = useState(false);
  const [investigationsError, setInvestigationsError] = useState<string>("");

  const [investigationQuery, setInvestigationQuery] = useState("");
  const investigationQueryTrimmed = investigationQuery.trim().toLowerCase();

  // Load conditions
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setConditionsLoading(true);
      setConditionsError("");

      try {
        const res = await fetch("/api/conditions", {
          headers: { Accept: "application/json" },
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(
            text || `Failed to load conditions (status ${res.status})`
          );
        }

        const data = await res.json();
        if (!Array.isArray(data)) {
          throw new Error("Unexpected response format from /api/conditions");
        }

        if (!cancelled) setConditions(data);
      } catch (e: any) {
        if (!cancelled) {
          setConditions([]);
          setConditionsError(e?.message || "Failed to load conditions.");
        }
      } finally {
        if (!cancelled) setConditionsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load investigations
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setInvestigationsLoading(true);
      setInvestigationsError("");

      try {
        const res = await fetch("/api/investigations", {
          headers: { Accept: "application/json" },
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(
            text || `Failed to load investigations (status ${res.status})`
          );
        }

        const data = await res.json();
        if (!Array.isArray(data)) {
          throw new Error("Unexpected response format from /api/investigations");
        }

        if (!cancelled) setInvestigations(data);
      } catch (e: any) {
        if (!cancelled) {
          setInvestigations([]);
          setInvestigationsError(e?.message || "Failed to load investigations.");
        }
      } finally {
        if (!cancelled) setInvestigationsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const conditionOptions = useMemo(() => {
    return conditions
      .filter((c) => typeof c?.ConditionID === "number")
      .map((c) => ({
        id: String(c.ConditionID),
        name:
          c.Name && c.Name.trim()
            ? c.Name.trim()
            : `Condition ${c.ConditionID}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [conditions]);

  const togglePrimary = (id: string) => {
    const current = Array.isArray(primaryConditionIds) ? primaryConditionIds : [];
    const exists = current.includes(id);

    const updated = exists ? current.filter((x) => x !== id) : [...current, id];

    // Ensure it can't be in both lists at once
    const excludedCurrent = Array.isArray(excludedConditionIds)
      ? excludedConditionIds
      : [];
    const excludedUpdated = excludedCurrent.filter((x) => x !== id);

    updateConditionsField("primaryConditionIds", updated);
    updateConditionsField("excludedConditionIds", excludedUpdated);
  };

  const toggleExcluded = (id: string) => {
    const current = Array.isArray(excludedConditionIds) ? excludedConditionIds : [];
    const exists = current.includes(id);

    const updated = exists ? current.filter((x) => x !== id) : [...current, id];

    // Ensure it can't be in both lists at once
    const primaryCurrent = Array.isArray(primaryConditionIds)
      ? primaryConditionIds
      : [];
    const primaryUpdated = primaryCurrent.filter((x) => x !== id);

    updateConditionsField("excludedConditionIds", updated);
    updateConditionsField("primaryConditionIds", primaryUpdated);
  };

  const filteredPrimaryOptions = useMemo(() => {
    const q = primarySearch.trim().toLowerCase();
    if (!q) return conditionOptions;
    return conditionOptions.filter((c) => c.name.toLowerCase().includes(q));
  }, [conditionOptions, primarySearch]);

  const filteredExcludedOptions = useMemo(() => {
    const q = excludedSearch.trim().toLowerCase();
    if (!q) return conditionOptions;
    return conditionOptions.filter((c) => c.name.toLowerCase().includes(q));
  }, [conditionOptions, excludedSearch]);

  const selectedInvestigationSet = useMemo(
    () => new Set(selectedInvestigationIds),
    [selectedInvestigationIds]
  );

  const investigationsById = useMemo(() => {
    const map = new Map<string, string>();
    investigations.forEach((inv) => {
      const id = String(inv.InvestigationID);
      const name =
        inv.Name && inv.Name.trim()
          ? inv.Name.trim()
          : `Investigation ${inv.InvestigationID}`;
      map.set(id, name);
    });
    return map;
  }, [investigations]);

  const selectedInvestigationItems = useMemo(() => {
    return selectedInvestigationIds.map((id) => ({
      id,
      name: investigationsById.get(id) || `Investigation ${id}`,
    }));
  }, [selectedInvestigationIds, investigationsById]);

  const filteredInvestigationOptions = useMemo(() => {
    const base = investigations
      .map((inv) => ({
        id: String(inv.InvestigationID),
        name:
          inv.Name && inv.Name.trim()
            ? inv.Name.trim()
            : `Investigation ${inv.InvestigationID}`,
      }))
      .filter((x) => !selectedInvestigationSet.has(x.id));

    if (!investigationQueryTrimmed) return base.slice(0, 40);

    return base
      .filter((x) => x.name.toLowerCase().includes(investigationQueryTrimmed))
      .slice(0, 40);
  }, [investigations, investigationQueryTrimmed, selectedInvestigationSet]);

  const persistSelectedInvestigations = (ids: string[]) => {
    updateReferralField("requiredInvestigationIds", arrayToCsv(ids));
  };

  const addInvestigation = (id: string) => {
    const next = Array.from(new Set([...selectedInvestigationIds, id]));
    persistSelectedInvestigations(next);
    setInvestigationQuery("");
  };

  const removeInvestigation = (id: string) => {
    const next = selectedInvestigationIds.filter((x) => x !== id);
    persistSelectedInvestigations(next);
  };

  const boxStyle: React.CSSProperties = {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 12,
    background: "#fafafa",
    maxHeight: 260,
    overflowY: "auto",
  };

  const pillStyle = (checked: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 16,
    border: checked ? "2px solid #0017a2" : "1px solid #ccc",
    backgroundColor: checked ? "#eef1ff" : "#fff",
    cursor: "pointer",
    fontSize: 14,
  });

  const invBoxStyle: React.CSSProperties = {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 12,
    background: "#fafafa",
  };

  const invChipStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid #ccc",
    background: "#fff",
    fontSize: 14,
  };

  const invChipBtnStyle: React.CSSProperties = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 16,
    lineHeight: "16px",
  };

  const invItemStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #e5e5e5",
    background: "#fff",
    cursor: "pointer",
  };

  return (
    <div>
      <p style={{ marginBottom: 16 }}>
        Define the specialist’s <strong>clinical scope</strong> and any{" "}
        <strong>required investigations</strong> that should be completed before
        referral.
      </p>

      {/* CONDITIONS */}
      <div style={{ marginBottom: 22 }}>
        <h3 style={{ marginTop: 0, marginBottom: 10 }}>Conditions</h3>

        {conditionsError && (
          <div style={{ color: "red", marginBottom: 12 }}>{conditionsError}</div>
        )}
        {conditionsLoading && (
          <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 12 }}>
            Loading conditions…
          </div>
        )}

        {/* Primary focus */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", marginBottom: 6 }}>
            Primary focus conditions (optional)
          </label>

          <input
            type="text"
            value={primarySearch}
            onChange={(e) => setPrimarySearch(e.target.value)}
            placeholder="Type to search…"
            style={{
              width: "100%",
              padding: 8,
              boxSizing: "border-box",
              marginBottom: 10,
            }}
          />

          <div style={boxStyle}>
            {filteredPrimaryOptions.length === 0 ? (
              <div style={{ fontSize: 13, opacity: 0.8 }}>
                No matching conditions.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {filteredPrimaryOptions.map((opt) => {
                  const checked = (primaryConditionIds || []).includes(opt.id);
                  const disabled = (excludedConditionIds || []).includes(opt.id);

                  return (
                    <label
                      key={opt.id}
                      style={{
                        ...pillStyle(checked),
                        opacity: disabled ? 0.55 : 1,
                        cursor: disabled ? "not-allowed" : "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => togglePrimary(opt.id)}
                      />
                      <span>{opt.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Excluded */}
        <div style={{ marginBottom: 6 }}>
          <label style={{ display: "block", marginBottom: 6 }}>
            Excluded conditions (optional)
          </label>

          <input
            type="text"
            value={excludedSearch}
            onChange={(e) => setExcludedSearch(e.target.value)}
            placeholder="Type to search…"
            style={{
              width: "100%",
              padding: 8,
              boxSizing: "border-box",
              marginBottom: 10,
            }}
          />

          <div style={boxStyle}>
            {filteredExcludedOptions.length === 0 ? (
              <div style={{ fontSize: 13, opacity: 0.8 }}>
                No matching conditions.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {filteredExcludedOptions.map((opt) => {
                  const checked = (excludedConditionIds || []).includes(opt.id);
                  const disabled = (primaryConditionIds || []).includes(opt.id);

                  return (
                    <label
                      key={opt.id}
                      style={{
                        ...pillStyle(checked),
                        opacity: disabled ? 0.55 : 1,
                        cursor: disabled ? "not-allowed" : "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleExcluded(opt.id)}
                      />
                      <span>{opt.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* INVESTIGATIONS */}
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ marginTop: 0, marginBottom: 10 }}>Required investigations</h3>

        {(investigationsError || investigationsLoading) && (
          <div style={{ marginBottom: 10, fontSize: 13 }}>
            {investigationsLoading ? (
              <span style={{ opacity: 0.8 }}>Loading investigations…</span>
            ) : (
              <span style={{ color: "red" }}>{investigationsError}</span>
            )}
          </div>
        )}

        <div style={invBoxStyle}>
          <div style={{ marginBottom: 10, fontWeight: 700 }}>Selected</div>

          {selectedInvestigationItems.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              No required investigations selected.
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {selectedInvestigationItems.map((x) => (
                <span key={x.id} style={invChipStyle}>
                  {x.name}
                  <button
                    type="button"
                    onClick={() => removeInvestigation(x.id)}
                    style={invChipBtnStyle}
                    aria-label={`Remove ${x.name}`}
                    title="Remove"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Add from master list</div>
            <input
              type="text"
              value={investigationQuery}
              onChange={(e) => setInvestigationQuery(e.target.value)}
              placeholder="Type to search investigations…"
              style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
            />

            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
              {filteredInvestigationOptions.length === 0 ? (
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  {investigationQueryTrimmed ? "No matches." : "Start typing to search."}
                </div>
              ) : (
                filteredInvestigationOptions.map((x) => (
                  <div
                    key={x.id}
                    style={invItemStyle}
                    onClick={() => addInvestigation(x.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") addInvestigation(x.id);
                    }}
                    title="Click to add"
                  >
                    <span>{x.name}</span>
                    <span style={{ opacity: 0.6, fontSize: 12 }}>Add</span>
                  </div>
                ))
              )}
            </div>

            <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
              Choose any investigations you require before referral (labs, imaging, etc.).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabConditionsInvestigations;
